import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Loader2, Sparkles, Download, Wand2, ChevronLeft, ChevronRight, FileText,
  Layout as LayoutIcon, Palette, Type,
} from 'lucide-react';
import { toast } from 'sonner';

import { AppShell } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

import {
  standaloneCarouselDesign,
  standaloneCarouselDownloadPdf,
} from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import CarouselCardPreview from '../components/results/CarouselCardPreview';
import { CarouselBrandingControls } from '../components/results/CarouselBrandingControls';

// Mirror options from CarouselGeneratorDialog so the look-and-feel is consistent.
const TONES = [
  { value: 'professional', label: 'Professional', hint: 'Crisp, analytical, executive-grade' },
  { value: 'bold',         label: 'Bold',         hint: 'Confident, opinionated, punchy' },
  { value: 'minimalist',   label: 'Minimalist',   hint: 'Spare typography, lots of whitespace' },
  { value: 'premium',      label: 'Premium',      hint: 'Editorial gravitas, magazine feel' },
];

const DENSITIES = [
  { value: 'concise', label: 'Concise', hint: 'Fewer cards, sharper headlines' },
  { value: 'medium',  label: 'Medium',  hint: 'More cards, more depth' },
];

// Designer-mode style presets. Each preset suggests a font + accent default
// (the user can still override the accent below). Server source of truth:
// backend/auto_carousel_service.DESIGNER_STYLE_PRESETS.
const STYLE_PRESETS = [
  { id: 'premium',   label: 'Premium',   hint: 'Editorial, restrained, gallery feel',  font: 'playfair', accent: 'slate'  },
  { id: 'corporate', label: 'Corporate', hint: 'Polished consulting-deck clarity',     font: 'inter',    accent: 'indigo' },
  { id: 'academic',  label: 'Academic',  hint: 'Journal-paper, evidence-led',           font: 'playfair', accent: 'teal'   },
  { id: 'playful',   label: 'Playful',   hint: 'Warm, rounded, friendly',               font: 'opensans', accent: 'rose'   },
  { id: 'tech',      label: 'Tech',      hint: 'Geometric, product-led, startup',       font: 'inter',    accent: 'violet' },
];

const FONT_OPTIONS = [
  { value: 'inter',    label: 'Inter' },
  { value: 'opensans', label: 'Open Sans' },
  { value: 'playfair', label: 'Playfair Display' },
];

// Backend ACCENT_PALETTES — keyed presets supported by the HTML/Chromium renderer.
const ACCENT_SWATCHES = [
  { id: 'indigo',  label: 'Indigo',  hex: '#4F46E5' },
  { id: 'emerald', label: 'Emerald', hex: '#10B981' },
  { id: 'rose',    label: 'Rose',    hex: '#E11D48' },
  { id: 'amber',   label: 'Amber',   hex: '#F59E0B' },
  { id: 'slate',   label: 'Slate',   hex: '#0F172A' },
  { id: 'violet',  label: 'Violet',  hex: '#7C3AED' },
  { id: 'teal',    label: 'Teal',    hex: '#0D9488' },
  { id: 'sky',     label: 'Sky',     hex: '#0EA5E9' },
];

// Premium HTML templates — mirror carousel_html_service.PREMIUM_TEMPLATES.
// When one of these is selected, the download bypasses the Designer's
// auto-layout pipeline and produces a polished editorial PDF instead.
const PREMIUM_TEMPLATES = [
  {
    id: '',
    label: 'Designer Default',
    desc: 'Auto-laid Designer pipeline (uses Style + Font + Accent above).',
    accent: '#4F46E5',
    fg: '#0a0a0a',
    bg: '#ffffff',
  },
  {
    id: 'editorial_steps',
    label: 'Editorial Steps',
    desc: 'Two-line accented headline, step chip, big icon medallion, dark cover & closing — inspired by top LinkedIn carousels. Honors your Brand color.',
    accent: '#2A6F53',
    fg: '#FAFAF7',
    bg: '#0F1F17',
  },
  {
    id: 'editorial_spread',
    label: 'Editorial Spread',
    desc: 'Magazine serif, drop caps, terracotta accent.',
    accent: '#B04A23',
    fg: '#161512',
    bg: '#FBF8F1',
  },
  {
    id: 'stat_hero',
    label: 'Stat Hero',
    desc: 'Bold dark canvas, lemon accent, one huge stat per slide.',
    accent: '#F7E26B',
    fg: '#E9E7E0',
    bg: '#0F1116',
  },
  {
    id: 'mono_editorial',
    label: 'Mono Editorial',
    desc: 'B&W restraint, framed Newsreader serif, italic pull-quotes.',
    accent: '#0A0A0A',
    fg: '#0A0A0A',
    bg: '#FFFFFF',
  },
  {
    id: 'gradient_modern',
    label: 'Gradient Modern',
    desc: 'Vivid sunrise gradients, soft glass cards, bold Inter display.',
    accent: '#FF5A45',
    fg: '#0E1116',
    bg: 'linear-gradient(135deg,#ffd9c0 0%,#ffaa83 38%,#c87de6 78%,#6f5ce5 100%)',
  },
];

export default function CarouselDesignerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  // When routed from a post's "Design carousel" / "Build carousel slides"
  // button, ``location.state`` carries the post's text so we can prefill the
  // wizard. ``sourceMeta`` is a lightweight badge context ("From Post #N").
  const routedFrom = location.state || {};
  const initialText = typeof routedFrom.text === 'string' ? routedFrom.text : '';
  const routedSourceMeta = routedFrom.sourceMeta || null;

  const [text, setText] = useState(initialText);
  const [sourceMeta, setSourceMeta] = useState(routedSourceMeta);
  const [tone, setTone] = useState('professional');
  const [density, setDensity] = useState('concise');
  const [stylePreset, setStylePreset] = useState('corporate');
  const [font, setFont] = useState('inter');
  const [accentId, setAccentId] = useState('indigo');
  const [brandLabel, setBrandLabel] = useState('');

  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [design, setDesign] = useState(null);
  const [resolvedBrandLabel, setResolvedBrandLabel] = useState('');
  const [resolvedCardCount, setResolvedCardCount] = useState(0);
  const [selectedCardIdx, setSelectedCardIdx] = useState(0);

  // Phase 6 branding state (deck-wide)
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [logoPosition, setLogoPosition] = useState('header-left');
  const [bgDataUrl, setBgDataUrl] = useState(null);
  const [bgOpacity, setBgOpacity] = useState(0.18);

  // Premium template override — empty string means "Designer Default"
  // (auto-layout pipeline). Any other value routes the PDF render through
  // carousel_html_service.PREMIUM_TEMPLATES for an editorial PDF.
  const [premiumTemplate, setPremiumTemplate] = useState('');

  // User-picked brand color (hex). Currently honored by the
  // `editorial_steps` premium template — other templates ignore it and
  // fall back to their signature palette.
  const [brandColor, setBrandColor] = useState('#2A6F53');

  const accentHex = useMemo(
    () => (ACCENT_SWATCHES.find((s) => s.id === accentId)?.hex || '#4F46E5'),
    [accentId],
  );

  // If we were routed here from a post's "Design carousel" / "Build carousel
  // slides" button, surface a toast the first time and clear the router
  // state so subsequent reloads start fresh.
  useEffect(() => {
    if (routedFrom && (routedFrom.text || routedFrom.sourceMeta)) {
      const label = routedFrom.sourceMeta?.label || 'your post';
      toast.success(`Loaded ${label} into the Designer — hit Generate carousel when ready.`);
      // Clear the router state so it isn't re-applied on refresh
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live estimate of expected card count based on text length (matches backend heuristic)
  const estimatedCards = useMemo(() => {
    const n = (text || '').trim().length;
    if (n < 80) return 0;
    if (n < 600) return 6;
    if (n < 1500) return 7;
    if (n < 3000) return 8;
    if (n < 5000) return 9;
    return 10;
  }, [text]);

  const charCount = (text || '').trim().length;
  const canGenerate = charCount >= 80 && !busy;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setBusy(true);
    setSelectedCardIdx(0);
    const t = toast.loading('Designing your carousel… ~10–25s');
    try {
      const data = await standaloneCarouselDesign({
        text,
        tone,
        density,
        accentColor: accentId,
        font,
        brandLabel: brandLabel || undefined,
        stylePreset,
        mode: 'designer',
      });
      setDesign(data.design || null);
      setResolvedBrandLabel(data.brand_label || '');
      setResolvedCardCount(data.card_count || (data.design?.cards?.length ?? 0));
      toast.success(`Designed ${data.design?.cards?.length ?? 0} slides.`, { id: t });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not design carousel'), { id: t });
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!design || downloading) return;
    setDownloading(true);
    const t = toast.loading('Rendering premium PDF (Chromium)… ~10–20s');
    try {
      await standaloneCarouselDownloadPdf({
        design,
        brandLabel: resolvedBrandLabel || brandLabel || undefined,
        brandLogo: logoDataUrl || undefined,
        accentColor: accentId,
        font,
        logoPosition,
        backgroundImage: bgDataUrl || undefined,
        backgroundOpacity: bgOpacity,
        template: premiumTemplate || undefined,
        brandColor: brandColor || undefined,
        filenameHint: design?.title || 'carousel-designer',
      });
      toast.success('Downloaded.', { id: t });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not download PDF'), { id: t });
    } finally {
      setDownloading(false);
    }
  };

  const cards = design?.cards || [];
  const total = cards.length;
  const currentCard = cards[selectedCardIdx];

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="carousel-designer-page">
        {/* Hero */}
        <div>
          <Badge className="bg-card border border-border text-foreground mb-3" data-testid="carousel-designer-eyebrow">
            <Wand2 size={12} className="mr-1.5" /> Open Carousel Designer
          </Badge>
          {sourceMeta?.label && (
            <Badge
              variant="secondary"
              className="ml-2 mb-3 bg-primary/10 text-primary border-primary/20"
              data-testid="carousel-designer-source-badge"
            >
              <Sparkles size={11} className="mr-1.5" /> {sourceMeta.label}
            </Badge>
          )}
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Paste anything. Get a premium carousel.
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Drop in a brief, an article, or notes. We&apos;ll design a polished LinkedIn-ready carousel
            with gradients, infographic layouts, and accent highlights — rendered as a premium PDF
            via headless Chromium.
          </p>
          <p className="text-[11px] text-muted-foreground mt-2 max-w-2xl">
            Counts against your monthly carousel quota (6/month on the Executive Thought Leadership Plan, shared with Auto-Carousel generated from analyses).
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr,520px] gap-6">
          {/* LEFT COLUMN — CONTROLS */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6 space-y-5">
                {/* Paste box */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold" htmlFor="carousel-text">
                      <FileText size={14} className="inline mr-1.5 -mt-0.5" />
                      Paste your content
                    </Label>
                    <span
                      className={`text-[11px] font-mono ${charCount < 80 ? 'text-muted-foreground' : 'text-foreground'}`}
                      data-testid="carousel-designer-char-count"
                    >
                      {charCount} chars{estimatedCards ? ` · ~${estimatedCards} slides` : ''}
                    </span>
                  </div>
                  <Textarea
                    id="carousel-text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Paste a draft, article, brief, or notes (min ~80 characters). We'll auto-decide the right slide count based on length."
                    rows={10}
                    className="font-mono text-[13px] leading-relaxed"
                    data-testid="carousel-designer-text-input"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Tip: paste 600–3,000 characters for a balanced 7–8 slide deck.
                  </p>
                </div>

                {/* Style Preset (Designer mode) ----------------------------- */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <LayoutIcon size={14} className="text-primary" />
                    <Label className="text-sm font-semibold">Style preset</Label>
                  </div>
                  <p className="text-[11px] text-muted-foreground -mt-1">
                    Picks the font pairing, accent palette, and visual mood for the deck. You can fine-tune the accent below.
                  </p>
                  <ToggleGroup
                    type="single"
                    value={stylePreset}
                    onValueChange={(v) => {
                      if (!v) return;
                      setStylePreset(v);
                      // Auto-apply the preset's recommended font + accent. The
                      // user can override afterwards using the swatches below.
                      const p = STYLE_PRESETS.find((s) => s.id === v);
                      if (p) {
                        setFont(p.font);
                        setAccentId(p.accent);
                      }
                    }}
                    className="grid grid-cols-2 sm:grid-cols-5 gap-2"
                    data-testid="carousel-designer-style-preset"
                  >
                    {STYLE_PRESETS.map((p) => (
                      <ToggleGroupItem
                        key={p.id}
                        value={p.id}
                        className="flex-col h-auto py-3 px-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-lg border border-border"
                        data-testid={`carousel-designer-preset-${p.id}`}
                      >
                        <span className="text-xs font-semibold">{p.label}</span>
                        <span className="text-[10px] opacity-70 mt-0.5 leading-tight text-center px-1">{p.hint}</span>
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>

                {/* Premium Template Override --------------------------------- */}
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <Label className="text-sm font-semibold">Premium Template</Label>
                    <span className="text-[10px] font-mono tracking-[0.18em] uppercase text-muted-foreground">PDF render</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground -mt-1">
                    Pick a polished editorial layout to swap in at download time. The Designer preview on the right stays the same — the polish lands in the PDF.
                  </p>
                  <ToggleGroup
                    type="single"
                    value={premiumTemplate || '_default'}
                    onValueChange={(v) => setPremiumTemplate(v && v !== '_default' ? v : '')}
                    className="grid grid-cols-2 sm:grid-cols-5 gap-2"
                    data-testid="carousel-designer-premium-template"
                  >
                    {PREMIUM_TEMPLATES.map((p) => {
                      const value = p.id || '_default';
                      const isSelected = (premiumTemplate || '') === p.id;
                      return (
                        <ToggleGroupItem
                          key={value}
                          value={value}
                          className="flex-col h-auto py-0 px-0 overflow-hidden rounded-lg border data-[state=on]:border-primary data-[state=on]:ring-2 data-[state=on]:ring-primary/30 relative"
                          data-testid={`carousel-designer-premium-${p.id || 'default'}`}
                        >
                          <div
                            className="w-full aspect-[4/5] flex flex-col justify-between p-2 relative"
                            style={{ background: p.bg, color: p.fg }}
                          >
                            {p.id && (
                              <span
                                className="absolute top-1 right-1 px-1 py-0.5 rounded text-[7px] font-semibold tracking-[0.18em] uppercase"
                                style={{ backgroundColor: 'rgba(0,0,0,0.78)', color: '#f7e26b' }}
                              >
                                Pro
                              </span>
                            )}
                            <span
                              className="text-[7px] tracking-[0.18em]"
                              style={{ fontFamily: '"JetBrains Mono",monospace', color: p.accent }}
                            >
                              01 / 06
                            </span>
                            <div className="absolute left-0 top-4 bottom-4 w-0.5" style={{ backgroundColor: p.accent }} />
                            <div className="pl-1.5">
                              <p className="text-[8px] font-bold leading-tight" style={{ color: p.fg }}>
                                Headline
                              </p>
                              {isSelected && (
                                <p className="text-[6px] mt-0.5 opacity-70">Selected</p>
                              )}
                            </div>
                          </div>
                          <div className="w-full px-1.5 py-1 bg-card border-t border-border">
                            <p className="text-[10px] font-medium truncate text-foreground" title={p.label}>{p.label}</p>
                          </div>
                        </ToggleGroupItem>
                      );
                    })}
                  </ToggleGroup>
                  {premiumTemplate && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      <span className="font-medium text-foreground">{PREMIUM_TEMPLATES.find((p) => p.id === premiumTemplate)?.label}</span> · {PREMIUM_TEMPLATES.find((p) => p.id === premiumTemplate)?.desc}
                    </p>
                  )}
                </div>

                {/* Brand color picker — currently honored by editorial_steps
                    template. Other templates ignore it and use their own
                    signature palette. */}
                {premiumTemplate === 'editorial_steps' && (
                  <div className="space-y-2" data-testid="carousel-designer-brand-color-block">
                    <Label className="text-sm font-semibold">Brand color</Label>
                    <div className="flex items-center gap-3">
                      <label
                        className="relative w-12 h-12 rounded-xl border border-border shadow-sm cursor-pointer overflow-hidden"
                        style={{ backgroundColor: brandColor }}
                        aria-label="Pick brand color"
                        data-testid="carousel-designer-brand-color-swatch"
                      >
                        <input
                          type="color"
                          value={brandColor}
                          onChange={(e) => setBrandColor(e.target.value)}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          aria-label="Brand color"
                        />
                      </label>
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-[11px] font-mono text-muted-foreground">HEX</span>
                        <Input
                          value={brandColor}
                          onChange={(e) => {
                            const v = e.target.value.trim();
                            if (v.length <= 7) setBrandColor(v.startsWith('#') ? v : `#${v}`.slice(0, 7));
                          }}
                          className="font-mono text-sm h-9 max-w-[130px]"
                          placeholder="#2A6F53"
                          data-testid="carousel-designer-brand-color-input"
                        />
                        <div className="flex items-center gap-1.5 ml-1">
                          {['#2A6F53', '#0F62FE', '#B04A23', '#7C3AED', '#0F172A'].map((hex) => (
                            <button
                              key={hex}
                              type="button"
                              onClick={() => setBrandColor(hex)}
                              className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${brandColor.toLowerCase() === hex.toLowerCase() ? 'border-foreground' : 'border-transparent'}`}
                              style={{ backgroundColor: hex }}
                              title={hex}
                              aria-label={`Preset ${hex}`}
                              data-testid={`carousel-designer-brand-color-preset-${hex.slice(1).toLowerCase()}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Deck-wide accent for step chips, headline highlight, icon medallions, and closing lockup.
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Tone</Label>
                  <ToggleGroup
                    type="single"
                    value={tone}
                    onValueChange={(v) => v && setTone(v)}
                    className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                  >
                    {TONES.map((t) => (
                      <ToggleGroupItem
                        key={t.value}
                        value={t.value}
                        className="flex-col h-auto py-2.5 px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                        data-testid={`carousel-designer-tone-${t.value}`}
                      >
                        <span className="text-xs font-semibold">{t.label}</span>
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  <p className="text-[11px] text-muted-foreground">
                    {TONES.find((t) => t.value === tone)?.hint}
                  </p>
                </div>

                {/* Density */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Density</Label>
                  <ToggleGroup
                    type="single"
                    value={density}
                    onValueChange={(v) => v && setDensity(v)}
                    className="grid grid-cols-2 gap-2"
                  >
                    {DENSITIES.map((d) => (
                      <ToggleGroupItem
                        key={d.value}
                        value={d.value}
                        className="flex-col h-auto py-2.5 px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                        data-testid={`carousel-designer-density-${d.value}`}
                      >
                        <span className="text-xs font-semibold">{d.label}</span>
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>

                {/* Font + Brand Label row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Font</Label>
                    <Select value={font} onValueChange={setFont}>
                      <SelectTrigger data-testid="carousel-designer-font-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_OPTIONS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold" htmlFor="carousel-brand-label">Brand label (optional)</Label>
                    <Input
                      id="carousel-brand-label"
                      value={brandLabel}
                      onChange={(e) => setBrandLabel(e.target.value)}
                      placeholder="Leave blank to hide, or type your brand"
                      maxLength={60}
                      data-testid="carousel-designer-brand-input"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Upload your own logo below to replace this text. If both are empty, the top-left stays clean.
                    </p>
                  </div>
                </div>

                {/* Accent palette */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Accent color</Label>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                    {ACCENT_SWATCHES.map((s) => {
                      const active = s.id === accentId;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setAccentId(s.id)}
                          aria-label={s.label}
                          aria-pressed={active}
                          className={`h-10 rounded-lg border-2 transition-all ${
                            active ? 'border-foreground scale-105 shadow-md' : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: s.hex }}
                          data-testid={`carousel-designer-accent-${s.id}`}
                        />
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Selected: <span className="font-medium text-foreground">{ACCENT_SWATCHES.find((s) => s.id === accentId)?.label}</span>
                  </p>
                </div>

                {/* Branding (logo + bg + opacity) */}
                <div className="pt-2 border-t border-border">
                  <CarouselBrandingControls
                    logoDataUrl={logoDataUrl}
                    onLogoChange={setLogoDataUrl}
                    logoPosition={logoPosition}
                    onLogoPositionChange={setLogoPosition}
                    bgDataUrl={bgDataUrl}
                    onBgChange={setBgDataUrl}
                    bgOpacity={bgOpacity}
                    onBgOpacityChange={setBgOpacity}
                    testIdPrefix="carousel-designer-branding"
                  />
                </div>

                {/* Generate */}
                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                  <Button
                    onClick={handleGenerate}
                    disabled={!canGenerate}
                    className="flex-1 gap-1.5"
                    data-testid="carousel-designer-generate-btn"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {busy ? 'Designing…' : design ? 'Re-roll deck' : 'Generate carousel'}
                  </Button>
                  {design && (
                    <Button
                      variant="secondary"
                      onClick={handleDownload}
                      disabled={downloading || busy}
                      className="gap-1.5"
                      data-testid="carousel-designer-download-btn"
                    >
                      {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                      {downloading ? 'Rendering…' : 'Download PDF'}
                    </Button>
                  )}
                </div>
                {charCount > 0 && charCount < 80 && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="carousel-designer-too-short">
                    Paste at least 80 characters to generate.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* RIGHT COLUMN — PREVIEW */}
          <div>
            <Card className="sticky top-20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs font-medium tracking-wide text-muted-foreground">
                    PREVIEW
                  </Label>
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {total ? `${selectedCardIdx + 1} / ${total}` : 'Awaiting design'}
                  </Badge>
                </div>

                {!design && (
                  <div
                    className="aspect-[4/5] rounded-xl border-2 border-dashed border-border bg-muted/30 flex flex-col items-center justify-center text-center px-6"
                    data-testid="carousel-designer-empty-preview"
                  >
                    <Wand2 size={28} className="text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">Your carousel preview will appear here.</p>
                    <p className="text-xs text-muted-foreground mt-1.5 max-w-xs">
                      Paste content on the left, choose a style, and hit&nbsp;
                      <span className="font-medium text-foreground">Generate carousel</span>.
                    </p>
                  </div>
                )}

                {design && currentCard && (
                  <div className="space-y-3" data-testid="carousel-designer-preview-wrap">
                    {/* Design Brief panel (Designer mode) -------------------- */}
                    {design.brief && (
                      <div
                        className="rounded-xl border border-border bg-card/60 backdrop-blur-sm p-4 space-y-2"
                        data-testid="carousel-designer-brief"
                      >
                        <div className="flex items-start gap-2">
                          <FileText size={14} className="text-primary mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-muted-foreground">
                              Design Brief
                            </p>
                            <p className="text-sm font-semibold leading-snug mt-0.5" data-testid="carousel-designer-brief-title">
                              {design.brief.carousel_title || design.title || 'Untitled deck'}
                            </p>
                          </div>
                        </div>
                        {design.brief.visual_direction && (
                          <p className="text-[12px] leading-relaxed text-muted-foreground" data-testid="carousel-designer-brief-direction">
                            {design.brief.visual_direction}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {design.brief.style_preset && (
                            <Badge variant="outline" className="text-[10px]">
                              <Palette size={9} className="mr-1" />
                              {design.brief.style_preset.charAt(0).toUpperCase() + design.brief.style_preset.slice(1)}
                            </Badge>
                          )}
                          {design.brief.typography && (
                            <Badge variant="outline" className="text-[10px]">
                              <Type size={9} className="mr-1" />
                              {design.brief.typography}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-center">
                      <CarouselCardPreview
                        card={currentCard}
                        idx={selectedCardIdx + 1}
                        total={total}
                        deckTitle={design.title || ''}
                        deckSubtitle={design.subtitle || ''}
                        brandLabel={resolvedBrandLabel || brandLabel || ''}
                        brandLogo={logoDataUrl || ''}
                        accent={accentHex}
                        font={font}
                        width={380}
                      />
                    </div>

                    {/* Per-card breakdown (Designer mode design_notes) ----- */}
                    {currentCard?.design_notes && (
                      <div
                        className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5 text-[11px]"
                        data-testid="carousel-designer-card-notes"
                      >
                        <p className="text-[10px] font-mono tracking-[0.16em] uppercase text-muted-foreground">
                          Card {selectedCardIdx + 1} brief
                        </p>
                        {currentCard.layout_alias && (
                          <p>
                            <span className="font-medium text-foreground">Layout:</span>{' '}
                            <span className="text-muted-foreground">
                              {currentCard.layout_alias} <span className="opacity-50">({currentCard.layout})</span>
                            </span>
                          </p>
                        )}
                        {currentCard.design_notes.purpose && (
                          <p>
                            <span className="font-medium text-foreground">Purpose:</span>{' '}
                            <span className="text-muted-foreground">{currentCard.design_notes.purpose}</span>
                          </p>
                        )}
                        {currentCard.design_notes.visual_direction && (
                          <p>
                            <span className="font-medium text-foreground">Visual:</span>{' '}
                            <span className="text-muted-foreground">{currentCard.design_notes.visual_direction}</span>
                          </p>
                        )}
                        {currentCard.design_notes.font_pairing && (
                          <p>
                            <span className="font-medium text-foreground">Typography:</span>{' '}
                            <span className="text-muted-foreground">{currentCard.design_notes.font_pairing}</span>
                          </p>
                        )}
                        {currentCard.design_notes.image_prompt && (
                          <p>
                            <span className="font-medium text-foreground">Image prompt:</span>{' '}
                            <span className="text-muted-foreground italic">{currentCard.design_notes.image_prompt}</span>
                          </p>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCardIdx((i) => Math.max(0, i - 1))}
                        disabled={selectedCardIdx === 0}
                        className="gap-1"
                        data-testid="carousel-designer-prev-btn"
                      >
                        <ChevronLeft size={14} /> Prev
                      </Button>
                      <div className="flex gap-1.5">
                        {cards.map((_c, i) => (
                          <button
                            key={i}
                            type="button"
                            aria-label={`Go to slide ${i + 1}`}
                            onClick={() => setSelectedCardIdx(i)}
                            className={`h-2 rounded-full transition-all ${
                              i === selectedCardIdx
                                ? 'w-6 bg-foreground'
                                : 'w-2 bg-border hover:bg-muted-foreground'
                            }`}
                          />
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCardIdx((i) => Math.min(total - 1, i + 1))}
                        disabled={selectedCardIdx >= total - 1}
                        className="gap-1"
                        data-testid="carousel-designer-next-btn"
                      >
                        Next <ChevronRight size={14} />
                      </Button>
                    </div>

                    <div className="text-[11px] text-muted-foreground bg-muted/40 rounded-lg p-3 border border-border">
                      <p className="font-medium text-foreground mb-1">
                        <Sparkles size={11} className="inline mr-1" />
                        Preview is a low-fi sketch.
                      </p>
                      <p>
                        The downloaded PDF is rendered via headless Chromium with real gradients,
                        soft shadows, and SVG infographics — it will look significantly more polished
                        than this preview.
                      </p>
                    </div>

                    {resolvedCardCount > 0 && (
                      <p className="text-[11px] text-muted-foreground text-center">
                        Auto-decided {resolvedCardCount} slides from your text length.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
